import { CycleBudgetField } from 'nomst/Stores/Configuration/cycleBudgetField';
import { dateFormat } from 'common/date-operation';
import { AmountColor, AmountCode, LabAmountCodes } from 'typings/enums';
import { ICycleBudget } from 'typings/cycleBudget';
import { observable, computed, action, makeObservable } from 'mobx';
import { maid } from 'utils/MobxDataManager/singletons';

export class CycleBudget {
    id: number;
    cycleId: number;
    @observable name: string;
    @observable allowManagerOverBudget: boolean;
    @observable requireCommentWhenOverBudget: boolean;
    @observable useProration: boolean;
    @observable associatedFields: string[];
    @observable isBudgetHasAnyDecision: boolean;
    @observable amountsAlreadyHaveDecisions: string[];
    @observable cycleBudgetFields: CycleBudgetField[];
    @observable autoSync: boolean;
    @observable lastSync: string;
    budgetCycleId: number;
    @observable color: string;

    constructor(dto: ICycleBudget) {
        makeObservable(this);
        this.id = dto.id;
        this.cycleId = dto.cycleId;
        this.name = dto.name;
        this.allowManagerOverBudget = dto.allowManagerOverBudget;
        this.requireCommentWhenOverBudget = dto.requireCommentWhenOverBudget;
        this.useProration = dto.useProration;
        this.associatedFields = dto.associatedFields;
        this.isBudgetHasAnyDecision = dto.isBudgetHasAnyDecision;
        this.amountsAlreadyHaveDecisions = dto.amountsAlreadyHaveDecisions;
        this.cycleBudgetFields = [];
        dto.cycleBudgetFields?.forEach(field => {
            this.cycleBudgetFields.push(new CycleBudgetField(field, maid));
        });
        this.autoSync = dto.autoSync;
        this.lastSync = dto.lastSync;
        this.budgetCycleId = dto.budgetCycleId;
        this.color = dto.color;
    }

    @computed
    get topPriorityField(): CycleBudgetField {
        let topPriority: number;
        let topPriorityField: CycleBudgetField = {} as CycleBudgetField;
        this.cycleBudgetFields.forEach(budgetField => {
            if (!topPriority || budgetField.colorPriority < topPriority) {
                topPriority = budgetField.colorPriority;
                topPriorityField = budgetField;
            }
        });

        return topPriorityField;
    }

    @computed
    get lastSyncDateText(): string {
        if (!this.lastSync) {
            return 'N/A';
        }

        return dateFormat(this.lastSyncDateType(), 'MM/dd/yyyy');
    }

    @computed
    get hasAnyLabAmount(): boolean {
        return this.cycleBudgetFields.find(budgetField =>
            LabAmountCodes[budgetField.amountCode as string]) !== undefined;
    }

    lastSyncDateType(): Date {
        return new Date(this.lastSync);
    }

    private hasLabCode(code: AmountCode): boolean {
        switch (code) {
            case AmountCode.Merit:
            case AmountCode.Bonus:
            case AmountCode.VariablePay:
            case AmountCode.Stock:
                return true;
            default:
                return false;
        }
    }

    @computed
    get budgetClass(): string {
        if (this.color === AmountColor.Increase) {
            return 'budget-increase';
        }

        if (this.color === AmountColor.Bonus) {
            return 'budget-bonus';
        }

        if (this.color === AmountColor.MarketAdjustment) {
            return 'budget-market';
        }

        if (this.color === AmountColor.LumpSum) {
            return 'budget-lump';
        }

        if (this.color === AmountColor.Promotion) {
            return 'budget-promotion';
        }

        if (this.color === AmountColor.VariablePay) {
            return 'budget-variable';
        }

        if (this.color === AmountColor.Stock) {
            return 'budget-stock';
        }

        return '';
    }

    @computed
    get associatedFieldsText(): string {
        if (this.associatedFields.length === 0) {
            return 'None';
        }

        return this.associatedFields.join(', ');
    }

    @computed
    get isActive(): boolean {
        return this.associatedFields.length > 0;
    }

    @computed
    get isStockBudgetInUnitOnly(): boolean {
        return this.cycleBudgetFields.some(budgetField => budgetField.isUnitOnly);
    }

    @action
    handleBudgetNameChange(value: string): void {
        this.name = value;
    }

    @action
    handleAssociatedFieldsChange(value: string[]): void {
        this.associatedFields = value;
    }

    @action
    handleAllowManagerToOverBudget(value: boolean): void {
        this.allowManagerOverBudget = value;
        this.requireCommentWhenOverBudget = false;
    }

    @action
    handleRequireCommentOnOverBudget(value: boolean): void {
        this.requireCommentWhenOverBudget = value;
    }

    @action
    updateBudget(budget: CycleBudget): void {
        this.handleBudgetNameChange(budget.name);
        this.handleAssociatedFieldsChange(budget.associatedFields);
        this.handleAllowManagerToOverBudget(budget.allowManagerOverBudget);
        this.handleRequireCommentOnOverBudget(budget.requireCommentWhenOverBudget);
    }

    @action
    updateProration(useProration: boolean): void {
        this.useProration = useProration;
    }

    @action
    pushField(budgetField: CycleBudgetField): void {
        if (!budgetField) {
            return;
        }
        budgetField.setBudgetCycleId(this.budgetCycleId);
        this.cycleBudgetFields.push(budgetField);
    }

    @action
    removeAssociatedField(budgetField: CycleBudgetField): void {
        budgetField.setBudgetCycleId(null);
        this.cycleBudgetFields.filter(field => field.title !== budgetField.title);
        this.associatedFields.filter(field => field !== budgetField.title);
    }

    @action
    clearFields(clearBudgetCycleId: boolean = false): void {
        if (clearBudgetCycleId) {
            this.cycleBudgetFields.forEach((budgetField) => budgetField.setBudgetCycleId(null));
        }
        this.cycleBudgetFields = [];
    }

    @action
    setAutoSyncWithLab(value: boolean): void {
        this.autoSync = value;
    }

    @action
    setLastSyncDate(): void {
        this.lastSync = dateFormat(new Date());
    }

    @action
    updateColor(): void {
        const topPriorityField = this.topPriorityField;
        if (!topPriorityField) {
            return;
        }

        this.color = topPriorityField.color;
    }

    @action
    refreshBudgetCycleId(budgetCycleId: number): void {
        this.budgetCycleId = budgetCycleId;
        this.cycleBudgetFields.forEach(budgetField => budgetField.setBudgetCycleId(budgetCycleId));
    }

    @action
    bindFields(budgetFields: CycleBudgetField[]): void {
        budgetFields.forEach(budgetField => {
            if (budgetField.budgetCycleId === this.budgetCycleId) {
                this.cycleBudgetFields.push(budgetField);
            }
        });

        this.associatedFields =
            this.cycleBudgetFields.map(budgetField => budgetField.title);
    }
}